import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Truck, Gauge, Route, ShieldCheck, ArrowRight } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1766785286781-d3a0e20f6d7f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwZGF5bGlnaHR8ZW58MHx8fHwxNzgzMjc1Nzk0fDA&ixlib=rb-4.1.0&q=85";

const STATS = [
  { value: "8", label: "Diagnostic checkpoints" },
  { value: "AI", label: "Personalized readiness report" },
  { value: "2 min", label: "Average completion time" },
];

const STEPS = [
  { icon: Gauge, title: "Diagnose", text: "Answer 8 sharp questions about your operation, capital and compliance." },
  { icon: Route, title: "Score", text: "Our AI grades your business-launch readiness from 0 to 100." },
  { icon: ShieldCheck, title: "Decide", text: "Self-correct with the Guide — or delegate to Managed Services." },
];

export default function Landing({ onStart }) {
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="landing-brand-home" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900">
            <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
          </Link>
          <a href="/admin" data-testid="admin-link" className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 hover:text-slate-900 transition-colors">
            Admin
          </a>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-6">
            <span className="w-8 h-px bg-brand" /> RouteHaul Logistics Pathways
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-[0.95]">
            Are you actually ready to run your own freight business?
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl">
            Stop guessing. This diagnostic engine grades your real operational readiness and forces
            the decision every driver avoids — build your foundation, or start scaling.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button
              data-testid="start-quiz-button"
              onClick={onStart}
              className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold group"
            >
              Run my diagnostic
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <div className="flex items-center gap-6">
              {STATS.slice(0, 2).map((s) => (
                <div key={s.label}>
                  <div className="font-heading font-black text-2xl text-slate-900 tabular">{s.value}</div>
                  <div className="text-xs text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          className="relative"
        >
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-brand rounded-md z-0" />
          <img
            src={HERO}
            alt="Semi truck on highway"
            className="relative z-10 w-full h-[460px] object-cover rounded-md border border-slate-200"
          />
          <div className="absolute z-20 bottom-6 left-6 right-6 bg-slate-900/90 backdrop-blur border border-white/10 rounded-md p-5">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-1">Readiness Score</div>
            <div className="font-mono text-3xl font-bold text-white tabular">0 – 100</div>
          </div>
        </motion.div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">How it works</div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-12 max-w-2xl">
            Three checkpoints between you and your own authority.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white border border-slate-200 rounded-md p-8"
              >
                <div className="w-12 h-12 rounded-md bg-slate-900 flex items-center justify-center mb-6">
                  <s.icon className="w-6 h-6 text-brand" strokeWidth={2.2} />
                </div>
                <div className="font-mono text-xs text-slate-400 mb-2">0{i + 1}</div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-slate-600 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12">
            <Button
              data-testid="start-quiz-button-secondary"
              onClick={onStart}
              className="h-14 px-8 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-base font-bold"
            >
              Start the 2-minute diagnostic
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between">
          <span>© {new Date().getFullYear()} RouteHaul Logistics Pathways</span>
          <span className="font-mono">The 12-Step Business Foundation Guide</span>
        </div>
      </footer>
    </div>
  );
}
